const API_BASE = import.meta.env.VITE_API_BASE ?? '';

function storeForSW(key, value) {
  const req = indexedDB.open('labpsi-pwa', 1);
  req.onupgradeneeded = () => req.result.createObjectStore('kv');
  req.onsuccess = () => {
    const tx = req.result.transaction('kv', 'readwrite');
    tx.objectStore('kv').put(value, key);
  };
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

let _vapidKeyPromise = null;
function getVapidPublicKey() {
  if (!_vapidKeyPromise) {
    _vapidKeyPromise = fetch(`${API_BASE}/api/push/vapid-public-key`)
      .then((r) => r.json())
      .then((d) => d.publicKey);
  }
  return _vapidKeyPromise;
}

// El SW no tiene acceso a import.meta.env: le dejamos acá la URL base del back
// (puede vivir en otro dominio, p. ej. Firebase Hosting + servidor aparte).
// Se llama en cada carga de página (no solo al suscribirse) para que un
// dispositivo ya suscripto antes de este fix también quede "sanado".
export function primeServiceWorkerStorage(deviceId) {
  storeForSW('device_id', deviceId);
  storeForSW('api_base', API_BASE);
}

export async function subscribeToPush(deviceId, deviceType) {
  if (!isPushSupported()) throw new Error('Este navegador no soporta notificaciones push.');

  primeServiceWorkerStorage(deviceId);

  const publicKey = await getVapidPublicKey();
  if (!publicKey) throw new Error('No se pudo obtener la clave del servidor (vapid-public-key).');

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });
  const { endpoint, keys } = subscription.toJSON();

  const r = await fetch(`${API_BASE}/api/push/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ device_id: deviceId, device_type: deviceType, endpoint, keys }),
  });
  if (!r.ok) throw new Error(`El servidor rechazó la suscripción (HTTP ${r.status}).`);
}

export async function maybeAutoSubscribeToPush(deviceId, deviceType) {
  if (!isPushSupported()) return;
  if (localStorage.getItem('pwa_push_asked')) return;
  localStorage.setItem('pwa_push_asked', '1');

  try {
    if (Notification.permission !== 'default') return;
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      await subscribeToPush(deviceId, deviceType);
    }
  } catch { /* fire-and-forget */ }
}

export async function getDeviceStatus(deviceId) {
  const r = await fetch(`${API_BASE}/api/push/device-status?device_id=${encodeURIComponent(deviceId)}`);
  return r.json();
}

export async function toggleTestDevice(deviceId, label) {
  const r = await fetch(`${API_BASE}/api/push/mark-test`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ device_id: deviceId, label }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error ?? `Error ${r.status}`);
  return data;
}
